import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Shield, Users, Settings, FileText, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/components/auth/AuthProvider';

export const HRAdminPage = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();

  const handleBack = () => {
    navigate('/staff-dashboard');
  };

  const adminTools = [
    {
      title: 'User Management',
      description: 'Manage employee accounts, roles, and permissions',
      icon: Users,
      path: '/hr/user-management',
    },
    {
      title: 'System Settings',
      description: 'Configure system-wide HR settings and policies',
      icon: Settings,
      path: '/settings',
    },
    {
      title: 'Reports & Analytics',
      description: 'Access detailed HR reports and analytics',
      icon: FileText,
      path: '/reports',
    },
  ];

  return (
    <div className="min-h-screen bg-background p-6">
      {/* Header with Back Button */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="outline" onClick={handleBack} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back to Dashboard
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-foreground">HR Admin Tools</h1>
          <p className="text-muted-foreground">Secure administrative interface</p>
        </div>
      </div>

      {/* Security Notice Banner */}
      <Card className="mb-6 border-destructive/20 bg-destructive/5">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-destructive/10 rounded-full">
              <Shield className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <CardTitle className="text-destructive flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Administrative Access Required
              </CardTitle>
              <CardDescription>
                This area contains sensitive HR management tools. All actions are logged and monitored.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Current User Info */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Current Session</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Logged in as:</p>
              <p className="font-medium">{profile?.email}</p>
            </div>
            <Badge variant="destructive" className="gap-1">
              <Shield className="h-3 w-3" />
              ADMIN ACCESS
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Admin Tools Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {adminTools.map((tool) => {
          const IconComponent = tool.icon;
          
          return (
            <Card key={tool.title} className="hover:shadow-lg transition-shadow cursor-pointer group">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors">
                    <IconComponent className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{tool.title}</CardTitle>
                    <CardDescription className="text-sm">
                      {tool.description}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => navigate(tool.path)}
                >
                  Access Tool
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Security Footer */}
      <Card className="mt-8 border-muted bg-muted/5">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-muted-foreground mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Security Notice</p>
              <p className="text-xs text-muted-foreground">
                All administrative actions are logged for security and compliance purposes. 
                Unauthorized access attempts will be reported. Please follow company security policies 
                when accessing sensitive HR data.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};